import { client } from './client';
import { User, Consultation, DocumentItem, Recommendations } from '../types/cabinet.types';
import { SummaryReferral } from '../types/visit-summary.types';

export const getProfile = async (): Promise<User> => {
    const response = await client.get<User>('/users/me');
    return response.data;
};

export const updateProfile = async (data: Partial<User>): Promise<User> => {
    const response = await client.put<User>('/users/update_profile', data);
    return response.data;
};

export const getConsultations = async (): Promise<Consultation[]> => {
    const response = await client.get<Consultation[]>('/orders/consultations');
    return response.data;
};

export const getDocuments = async (): Promise<DocumentItem[]> => {
    const response = await client.get<DocumentItem[]>('/users/me/documents');
    return response.data;
};

export const uploadDocument = async (file: File): Promise<DocumentItem> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await client.post<DocumentItem>('/users/me/documents', formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
    return response.data;
};

/**
 * Видалення власного документа (WP documents або Medyc)
 * ID передається як string (наприклад, 'medyc_123') або number
 */
export const deleteDocument = async (id: string | number): Promise<void> => {
    await client.delete(`/users/me/documents/${id}`);
};

/**
 * Документи конкретного візиту (Medyc attachments)
 */
export const getVisitDocuments = async (visitId: string | number): Promise<DocumentItem[]> => {
    const response = await client.get<DocumentItem[]>(`/users/me/visit/${visitId}/documents`);
    return response.data;
};

/**
 * Залечення лікаря для конкретного візиту
 */
export const getRecommendations = async (visitId: string | number): Promise<Recommendations> => {
    const response = await client.get<Recommendations>(`/users/me/visit/${visitId}/recommendations`);
    return response.data;
};

export const sendVerificationEmail = async (): Promise<boolean> => {
    const lang = (typeof window !== 'undefined' && window.cabinetVars?.lang) ? window.cabinetVars.lang : 'pl';
    const response = await client.post('/users/verify_email/send', { lang });
    return response.status === 200;
};

export const fetchAvailableSlots = async (consultationId: string, from?: string, to?: string): Promise<any[]> => {
    let url = `/consultations/${consultationId}/available-slots`;
    if (from || to) {
        const params = new URLSearchParams();
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        url += `?${params.toString()}`;
    }
    const response = await client.get(url);
    return response.data;
};

export const rescheduleConsultation = async (consultationId: string, slotTime: string, doctorId?: string): Promise<void> => {
    const body: Record<string, any> = { slotTime };
    if (doctorId) body.doctorId = doctorId;
    await client.post(`/consultations/${consultationId}/reschedule`, body);
};

/**
 * Скерування для конкретного візиту (Medyc referrals)
 * Завантажується в фоні — не блокує рендер сторінки
 */
export const getVisitReferrals = async (visitId: string | number): Promise<SummaryReferral[]> => {
    const response = await client.get<SummaryReferral[]>(`/users/me/visit/${visitId}/referrals`);
    return response.data;
};

/**
 * Всі скерування пацієнта (Medyc referrals)
 * Щоб уникнути N+1 запитів для кожного візиту
 */
export const getPatientReferrals = async (): Promise<SummaryReferral[]> => {
    const response = await client.get<SummaryReferral[]>('/users/me/referrals');
    return response.data;
};

// ─── Checkout / PayU ──────────────────────────────────────────────────────────
import { getGa4CheckoutData } from '../utils/ga4Utils';

export interface CheckoutBilling {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface CheckoutProcessResponse {
  success: boolean;
  order_id: number;
  redirect_url?: string;
}

/**
 * Оформлення замовлення через PWA API.
 * Автоматично додає ga4_client_id та ga4_session_id з GA4 cookies
 * для серверної відправки purchase event в Google Analytics.
 */
export const checkoutProcess = async (billing: CheckoutBilling): Promise<CheckoutProcessResponse> => {
  const ga4Data = getGa4CheckoutData();

  const response = await client.post<CheckoutProcessResponse>('/checkout/process', {
    billing,
    // GA4 Measurement Protocol: передаємо дані сесії для точного атрибутування
    ga4_client_id: ga4Data.ga4_client_id,
    ga4_session_id: ga4Data.ga4_session_id,
  });

  return response.data;
};
